import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Camera, Loader2, Moon, Sun, Sunset } from "lucide-react";
import { api, pesanGalat } from "../../lib/api";
import { jamSekarangJakarta } from "../../lib/tanggal";
import LoginMascots from "./LoginMascots";
import useLoginEntrance from "./useLoginEntrance";
import MyAttendance from "../../pages/MyAttendance";
import "./LoginWelcome.css";

const URL = "/auth/daily-energy";
function initialPeriod() {
  const hour = Number(jamSekarangJakarta().slice(0, 2));
  return hour >= 5 && hour < 12 ? "morning" : hour >= 12 && hour < 18 ? "afternoon" : "night";
}

export default function LoginWelcome({user, branding, onDone, onLogout}) {
  const [context, setContext] = useState(null), [stage, setStage] = useState("hello");
  const [score, setScore] = useState(null);
  const [error, setError] = useState(""), [retry, setRetry] = useState(0), [saving, setSaving] = useState(false);
  const [savedScore, setSavedScore] = useState(null);
  const root = useRef(null);
  useLoginEntrance(root, ".eh-welcome-content > *", stage);
  const title = useRef(null), sending = useRef(false);
  const name = user.username || user.name || "teman";
  const period = context?.period || initialPeriod();
  const greeting = context?.greeting || {morning:"Good morning", afternoon:"Good afternoon", night:"Good night"}[period];
  const PeriodIcon = {morning:Sun, afternoon:Sunset, night:Moon}[period];

  useEffect(() => {
    let live = true;
    setError("");
    api.get(URL).then(({data}) => {if (live) setContext(data);}).catch(e => {if (live) setError(pesanGalat(e, "Check-in belum dapat dimuat."));});
    return () => {live = false;};
  }, [retry]);
  useEffect(() => {title.current?.focus();}, [stage]);

  const continueFromGreeting = () => {
    if (!context) return;
    if (context.entry) {
      setSavedScore(context.entry.score);
      setStage("recorded");
    } else setStage("energy");
  };

  const submit = async (e, clockIn = false) => {
    e.preventDefault();
    if (score === null || sending.current) return;
    sending.current = true; setSaving(true); setError("");
    try {
      const {data} = await api.post(URL, {score, date: context.date});
      setContext(data); setSavedScore(data.entry.score);
      setStage(clockIn && data.attendance?.eligible ? "attendance" : "thanks");
    } catch(e) {
      setError(pesanGalat(e, "Belum tersimpan. Coba lagi, ya."));
      if (e.response?.status === 409) {
        setContext(null); setStage("hello"); setRetry(v => v + 1);
      }
    } finally {sending.current = false; setSaving(false);}
  };

  return <main ref={root} className={`eh-welcome eh-welcome--${period}`} data-testid="login-welcome">
    <div className="eh-welcome-brand">{branding?.app_name || "EppleHub"}</div>
    <section className={`eh-welcome-card${stage === "attendance" ? " eh-welcome-card--attendance" : ""}`} aria-labelledby="welcome-title">
      <div className="eh-welcome-period"><PeriodIcon size={15}/><span>{period === "morning" ? "Awal yang baik" : period === "afternoon" ? "Jeda kecil, energi baru" : "Hangatnya malam"}</span></div>
      {stage !== "attendance" && <LoginMascots variant={stage === "thanks" ? "celebrate" : "idle"} energy={stage === "energy" ? score : savedScore} period={period} className="eh-welcome-mascots" />}
      {stage === "hello" && <div className="eh-welcome-content" key="hello">
        <h1 id="welcome-title" ref={title} tabIndex={-1} data-testid="welcome-greeting">{greeting},<span>{name}.</span></h1>
        <p>Senang bertemu kamu lagi.<br/>Kita mulai pelan, lalu lakukan yang terbaik.</p>
        <button className="eh-welcome-primary" data-testid="welcome-continue" onClick={continueFromGreeting} disabled={!context}>Yuk, lanjut <ArrowRight size={18}/></button>
      </div>}
      {stage === "energy" && <form className="eh-welcome-content" key="energy" onSubmit={submit}>
        <span className="eh-welcome-eyebrow">CHECK-IN HARI INI</span>
        <h1 id="welcome-title" ref={title} tabIndex={-1}>Seberapa semangat<br/>kamu hari ini?</h1>
        <p>Apa pun angkanya, tidak apa-apa.<br/>Pilih yang paling menggambarkan dirimu.</p>
        <div className="eh-welcome-score" aria-live="polite"><strong data-testid="energy-score">{score === null ? "—" : score}<small>%</small></strong><span>{score === null ? "Geser atau pilih persentase di bawah" : score <= 30 ? "Mulai dengan langkah kecil" : score <= 65 ? "Jalani dengan ritmemu" : "Siap untuk hari yang baik"}</span></div>
        <label className="sr-only" htmlFor="energy-range">Persentase semangat hari ini</label>
        <input id="energy-range" data-testid="energy-range" className="eh-welcome-range" type="range" min="0" max="100" step="1" value={score ?? 50} disabled={saving} onChange={e => setScore(Number(e.target.value))} aria-valuetext={score === null ? "Belum dipilih" : `${score} persen`} />
        <div className="eh-welcome-range-labels"><span>0% · Butuh waktu</span><span>100% · Penuh energi</span></div>
        <div className="eh-welcome-presets">{[25,50,75,100].map(v => <button type="button" key={v} disabled={saving} aria-pressed={score === v} onClick={() => setScore(v)}>{v}%</button>)}</div>
        <button className="eh-welcome-primary" data-testid="energy-submit" disabled={score === null || saving}>{saving ? <><Loader2 className="eh-welcome-spin" size={18}/> Menyimpan…</> : <>Simpan semangat <ArrowRight size={18}/></>}</button>
        {context.attendance?.eligible && !context.attendance.clock_in && <button type="button" className="eh-welcome-secondary" data-testid="energy-submit-attendance" disabled={score === null || saving} onClick={e => submit(e, true)}><Camera size={18}/> Simpan & absen masuk</button>}
        {context.attendance?.clock_in && <p className="eh-welcome-note">Absen masuk hari ini sudah tercatat.</p>}
        <p className="eh-welcome-note">Dicatat sekali sehari dan dapat dilihat pengelola.</p>
      </form>}
      {(stage === "thanks" || stage === "recorded") && <div className="eh-welcome-content" key={stage} data-testid="energy-confirmation">
        <span className="eh-welcome-saved" role="status"><Check size={18}/> {savedScore}% tersimpan hari ini</span>
        <h1 id="welcome-title" ref={title} tabIndex={-1}>{stage === "thanks" ? "Terima kasih," : "Selamat kembali,"}<span>{name}.</span></h1>
        <p>{stage === "thanks" ? "Semoga harimu berjalan baik." : "Semangatmu hari ini sudah dicatat. Siap melanjutkan?"}</p>
        {context?.attendance?.eligible && <button className="eh-welcome-secondary" data-testid="welcome-attendance" onClick={() => setStage("attendance")}><Camera size={18}/>{context.attendance.clock_in ? "Lihat absen hari ini" : "Absen masuk sekarang"}</button>}
        <button className="eh-welcome-primary" data-testid="welcome-enter-app" onClick={onDone}>Masuk ke EppleHub <ArrowRight size={18}/></button>
      </div>}
      {stage === "attendance" && <div className="eh-welcome-attendance" key="attendance" data-testid="welcome-attendance-panel">
        <h1 id="welcome-title" ref={title} tabIndex={-1}>Siap mulai hari ini?</h1>
        <p className="eh-welcome-saved"><Check size={18}/> Baterai {savedScore}% tersimpan</p>
        <p className="eh-welcome-attendance-note">Nilainya ikut tercatat di absensi. Ambil selfie dan konfirmasi untuk absen masuk.</p>
        <MyAttendance loginMode />
        <button className="eh-welcome-primary" data-testid="welcome-enter-app" onClick={onDone}>Lanjut ke EppleHub <ArrowRight size={18}/></button>
        <button className="eh-welcome-secondary" onClick={() => setStage("recorded")}>Kembali</button>
      </div>}
      {error && <div className="eh-welcome-error" role="alert"><p>{error}</p>{!context && <button onClick={() => setRetry(v => v + 1)}>Coba lagi</button>}<button onClick={onLogout}>Kembali ke login</button></div>}
    </section>
    <p className="eh-welcome-footer">Satu langkah kecil untuk hari yang lebih baik.</p>
  </main>;
}
